import { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { deMedicationFragmentInline$data } from "@/modules/patients/detail/medication/de/services/__generated__/deMedicationFragmentInline.graphql"
import { MEDICATION_MODES, MedicationMode } from "@/modules/patients/detail/medication/types/medication-mode"

type DeMedicationStoreState = {
  deMedicationMode: MedicationMode
  prevDeMode: MedicationMode
  existingMedication: deMedicationFragmentInline$data | null
  pharmaceuticalCentralNumberList: string[]
  setDeMedicationDrawerMode: (mode: MedicationMode) => void
  chooseSearchItem: () => void
  reset: () => void
  selectExistingMedication: (existingMedication: deMedicationFragmentInline$data) => void
  setPharmaceuticalCentralNumberList: (pharmaceuticalCentralNumberList: string[]) => void
}

const initialState = {
  deMedicationMode: null,
  prevDeMode: null,
  existingMedication: null,
  pharmaceuticalCentralNumberList: [],
}

function createDeMedicationStore() {
  return create<DeMedicationStoreState>((set, get) => ({
    ...initialState,
    setDeMedicationDrawerMode: (mode) =>
      set((state) => ({
        deMedicationMode: mode,
        prevDeMode: !mode && state.deMedicationMode === MEDICATION_MODES.VIEW ? null : state.deMedicationMode,
      })),
    chooseSearchItem: () => {
      const { prevDeMode, deMedicationMode } = get()
      let prevMode: MedicationMode = null
      if (
        (prevDeMode === MEDICATION_MODES.VIEW && deMedicationMode !== MEDICATION_MODES.VIEW) ||
        deMedicationMode === MEDICATION_MODES.VIEW
      ) {
        prevMode = MEDICATION_MODES.VIEW
      }
      if (prevDeMode === MEDICATION_MODES.CREATION && deMedicationMode === MEDICATION_MODES.CREATION) {
        prevMode = null
      }
      set(() => ({
        deMedicationMode: MEDICATION_MODES.CREATION,
        prevDeMode: prevMode,
      }))
    },
    reset() {
      set(initialState)
    },
    setPharmaceuticalCentralNumberList: (pharmaceuticalCentralNumberList: string[]) =>
      set({ pharmaceuticalCentralNumberList }),
    selectExistingMedication: (existingMedication) =>
      set(() => ({
        deMedicationMode: MEDICATION_MODES.EDIT,
        prevDeMode: MEDICATION_MODES.VIEW,
        existingMedication,
      })),
  }))
}

const DeMedicationContext = createContext<ReturnType<typeof createDeMedicationStore> | null>(null)

type DeMedicationProviderProps = React.PropsWithChildren

export const DeMedicationProvider = ({ children }: DeMedicationProviderProps) => {
  const storeRef = useRef<ReturnType<typeof createDeMedicationStore>>(undefined)
  if (!storeRef.current) {
    storeRef.current = createDeMedicationStore()
  }
  return <DeMedicationContext.Provider value={storeRef.current}>{children}</DeMedicationContext.Provider>
}

export function useDeMedicationContext<T>(selector: (state: DeMedicationStoreState) => T): T {
  const store = useContext(DeMedicationContext)
  if (!store) throw new Error("Missing DeMedicationProvider in the tree")
  return useStore(store, selector)
}
